import { useState } from 'react';
import { api, Credentials, WordPressInfo } from '../../api';
import { Alert, CredentialsBox, Loading, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

/** GET /services/:id/wordpress, plugins and maintenance mode — the wordpressManagement feature. */
function WordPressManagement({ base }: { base: string }) {
    const { data: wp, setData, error, loading } = useLoad(
        () => api.get<{ wordpress: WordPressInfo }>(`${base}/wordpress`).then(r => r.data.wordpress), [base]);
    const action = useAction();

    // Every WordPress endpoint returns the updated info.
    const mutate = (fn: () => Promise<{ data: { wordpress: WordPressInfo } }>) =>
        action.run(async () => setData((await fn()).data.wordpress));

    if (loading && !wp) return <div className="panel-card"><Loading /></div>;
    if (error || !wp) return <Alert type="error">{error ?? 'Could not load WordPress'}</Alert>;
    if (!wp.installed) return <div className="panel-card"><Alert type="info">WordPress is not installed on this site yet.</Alert></div>;

    const coreOutdated = wp.needsCoreUpdate;

    return (
        <>
            <div className="panel-card">
                <h3>Updates &amp; maintenance</h3>
                {action.error && <Alert type="error">{action.error}</Alert>}
                <div className="kv-grid" style={{ marginBottom: '1.25rem' }}>
                    <div><div className="kv-label">Version</div><div className="kv-value">{wp.version} {coreOutdated ? <span className="tag tag-progress">update {wp.latestVersion ?? 'available'}</span> : <span className="tag tag-completed">latest</span>}</div></div>
                    <div><div className="kv-label">Automatic updates</div><div className="kv-value">{wp.autoUpdate ? 'On' : 'Off'}</div></div>
                    <div><div className="kv-label">Maintenance mode</div><div className="kv-value">{wp.maintenanceMode ? 'On' : 'Off'}</div></div>
                </div>
                <div className="btn-row">
                    {coreOutdated && (
                        <button className="btn-submit inline small" disabled={action.busy} onClick={() => mutate(() => api.post(`${base}/wordpress/update-core`))}>
                            Update WordPress{wp.latestVersion ? ` to ${wp.latestVersion}` : ''}
                        </button>
                    )}
                    <button className="btn-outline small" disabled={action.busy}
                        onClick={() => mutate(() => api.put(`${base}/wordpress/maintenance`, { enabled: !wp.maintenanceMode }))}>
                        {wp.maintenanceMode ? 'Disable maintenance mode' : 'Enable maintenance mode'}
                    </button>
                </div>
            </div>

            <div className="panel-card">
                <h3>Plugins</h3>
                <div className="table-wrap">
                    <table className="data-table">
                        <thead><tr><th>Plugin</th><th>Version</th><th>Status</th><th /></tr></thead>
                        <tbody>
                            {wp.plugins.map(p => (
                                <tr key={p.slug}>
                                    <td>{p.name}</td>
                                    <td>{p.version} {p.updateAvailable && <span className="tag tag-progress">update available</span>}</td>
                                    <td>{p.active ? <span className="tag tag-completed">active</span> : <span className="tag tag-muted">inactive</span>}</td>
                                    <td className="actions">
                                        {p.updateAvailable && (
                                            <button className="btn-submit inline small" disabled={action.busy}
                                                onClick={() => mutate(() => api.post(`${base}/wordpress/plugins/${p.slug}/update`))}>Update</button>
                                        )}
                                        <button className="btn-outline small" disabled={action.busy}
                                            onClick={() => mutate(() => api.patch(`${base}/wordpress/plugins/${p.slug}`, { active: !p.active }))}>
                                            {p.active ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {wp.plugins.length === 0 && <tr><td colSpan={4} className="empty-state">No plugins installed.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {wp.themes.length > 0 && (
                <div className="panel-card">
                    <h3>Themes</h3>
                    <div className="table-wrap">
                        <table className="data-table">
                            <thead><tr><th>Theme</th><th>Version</th><th>Status</th></tr></thead>
                            <tbody>
                                {wp.themes.map(t => (
                                    <tr key={t.slug}>
                                        <td>{t.name}</td>
                                        <td>{t.version}</td>
                                        <td>{t.active ? <span className="tag tag-completed">active</span> : <span className="tag tag-muted">inactive</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}

/** One-click WP-Admin (POST …/wordpress/sso-token) and admin password reset — CarbonEdge B2B API. */
export default function WordPressTab({ base, detail }: TabProps) {
    const [credentials, setCredentials] = useState<Credentials | null>(null);
    const action = useAction();
    const adminUrl = `https://${detail.service.domain}/wp-admin`;

    // The tab is opened right away (before the request) so pop-up blockers allow it; the one-time
    // login link is loaded into it when CarbonEdge returns it.
    const openAdmin = () => {
        const tab = window.open('about:blank', '_blank');
        void action.run(async () => {
            try {
                const { sso } = (await api.post<{ sso: { loginUrl: string } }>(`${base}/wordpress/sso`)).data;
                if (tab) tab.location.href = sso.loginUrl;
                else window.location.href = sso.loginUrl;
            } catch (err) {
                tab?.close();
                throw err;
            }
        });
    };

    return (
        <>
            <div className="panel-card">
                <div className="panel-card-header">
                    <h3>WordPress</h3>
                    <button className="btn-submit inline small" onClick={openAdmin} disabled={action.busy || detail.service.status !== 'active'}>Open WP-Admin ↗</button>
                </div>
                {action.error && <Alert type="error">{action.error}</Alert>}
                <p className="card-text" style={{ marginBottom: '1rem' }}>
                    <strong>Open WP-Admin</strong> logs you straight in to <span className="mono">{adminUrl}</span> with a one-time link — no password needed.
                    To log in yourself, e.g. from a phone, generate a new password for the <span className="mono">admin</span> account.
                </p>
                {credentials ? <CredentialsBox credentials={credentials} onClose={() => setCredentials(null)} /> : (
                    <button className="btn-outline small" disabled={action.busy}
                        onClick={() => action.run(async () => setCredentials((await api.post<{ credentials: Credentials }>(`${base}/wordpress/reset-admin`)).data.credentials))}>
                        Reset admin password
                    </button>
                )}
            </div>
            {detail.capabilities.wordpressManagement && <WordPressManagement base={base} />}
        </>
    );
}
